import { useEffect, useState, type ReactNode } from 'react';
import { Modal } from './modal';
import { Input } from './input';
import { Button } from './button';

/**
 * A destructive-action confirmation modal that only enables its confirm button
 * once the user types an exact phrase (case-insensitive, trimmed) — the same guard
 * the account-deletion danger zone uses, extracted so other irreversible actions
 * (e.g. disconnecting an integration) get the same friction instead of a native
 * `window.confirm`. Resets the input whenever it (re)opens; Enter confirms once the
 * phrase matches.
 */
export function ConfirmByTypingModal({
  open,
  title,
  description,
  confirmPhrase,
  confirmLabel = 'Remove',
  pending = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** The exact text the user must type to enable the confirm button. */
  confirmPhrase: string;
  confirmLabel?: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const matches = value.trim().toLowerCase() === confirmPhrase.trim().toLowerCase();
  const confirm = () => {
    if (matches && !pending) onConfirm();
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={<h3 className="font-display text-base font-bold text-bone">{title}</h3>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm} disabled={!matches || pending}>
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        {description && <div className="text-muted">{description}</div>}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
            Type <strong className="text-bone">{confirmPhrase}</strong> to confirm
          </span>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirm();
            }}
            placeholder={confirmPhrase}
            autoFocus
          />
        </label>
      </div>
    </Modal>
  );
}
